<template>
  <div :class="rowClasses">
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed, PropType } from "vue";
import {
  RESPONSIVE_SPACING_VALUES,
  VALID_ALIGN_VALUES,
  VALID_FLEX_DIRECTION_VALUES,
  VALID_GAP_VALUES,
  VALID_GUTTER_VALUES,
  VALID_JUSTIFY_VALUES,
  VALID_MARGIN_VALUES,
  AlignType,
  FlexDirectionType,
  GapType,
  GutterType,
  JustifyType,
  MarginType,
} from "@/designSystem/Grid/constant";

const props = defineProps({
  noGutters: {
    type: Boolean,
    default: false,
  },

  gap: {
    type: String as PropType<GapType>,
    default: null,
    validator: (value: string | null): value is GapType =>
      value === null || VALID_GAP_VALUES.includes(value as GapType),
  },

  gapX: {
    type: String as PropType<GapType>,
    default: null,
    validator: (value: string | null): value is GapType =>
      value === null || VALID_GAP_VALUES.includes(value as GapType),
  },

  gapY: {
    type: String as PropType<GapType>,
    default: null,
    validator: (value: string | null): value is GapType =>
      value === null || VALID_GAP_VALUES.includes(value as GapType),
  },

  gutters: {
    type: String as PropType<GutterType>,
    default: null,
    validator: (value: string | null): value is GutterType =>
      value === null || VALID_GUTTER_VALUES.includes(value as GutterType),
  },

  guttersX: {
    type: String as PropType<GutterType>,
    default: null,
    validator: (value: string | null): value is GutterType =>
      value === null || VALID_GUTTER_VALUES.includes(value as GutterType),
  },

  guttersY: {
    type: String as PropType<GutterType>,
    default: null,
    validator: (value: string | null): value is GutterType =>
      value === null || VALID_GUTTER_VALUES.includes(value as GutterType),
  },

  marginTop: {
    type: String as PropType<MarginType>,
    default: null,
    validator: (value: string | null): value is MarginType =>
      value === null || VALID_MARGIN_VALUES.includes(value as MarginType),
  },

  alignItems: {
    type: String as PropType<AlignType>,
    default: null,
    validator: (value: string | null): value is AlignType =>
      value === null || VALID_ALIGN_VALUES.includes(value as AlignType),
  },

  justifyContent: {
    type: String as PropType<JustifyType>,
    default: null,
    validator: (value: string | null): value is JustifyType =>
      value === null || VALID_JUSTIFY_VALUES.includes(value as JustifyType),
  },

  flexDirection: {
    type: String as PropType<FlexDirectionType>,
    default: null,
    validator: (value: string | null): value is FlexDirectionType =>
      value === null || VALID_FLEX_DIRECTION_VALUES.includes(value as FlexDirectionType),
  },
});

const rowClasses = computed(() => {
  return [
    "row",
    !props.noGutters && props.gutters
      ? RESPONSIVE_SPACING_VALUES[props.gutters].map((value) => "g-" + value).join(" ")
      : "",
    !props.noGutters && props.guttersX
      ? RESPONSIVE_SPACING_VALUES[props.guttersX].map((value) => "gx-" + value).join(" ")
      : "",
    !props.noGutters && props.guttersY
      ? RESPONSIVE_SPACING_VALUES[props.guttersY].map((value) => "gy-" + value).join(" ")
      : "",
    props.alignItems ? `align-items-${props.alignItems}` : "",
    props.flexDirection ? `flex-${props.flexDirection}` : "",
    props.gap ? RESPONSIVE_SPACING_VALUES[props.gap].map((value) => "gap-" + value).join(" ") : "",
    props.gapX ? RESPONSIVE_SPACING_VALUES[props.gapX].map((value) => "gap-x-" + value).join(" ") : "",
    props.gapY ? RESPONSIVE_SPACING_VALUES[props.gapY].map((value) => "gap-y-" + value).join(" ") : "",
    props.marginTop ? RESPONSIVE_SPACING_VALUES[props.marginTop].map((value) => "mt-" + value).join(" ") : "",
    props.justifyContent ? `justify-content-${props.justifyContent}` : "",
    props.noGutters ? "g-0" : "",
  ]
    .filter(Boolean)
    .join(" ");
});
</script>

<style lang="scss" scoped>
@import "@/designSystem/style/main.scss";
@import "@/designSystem/Grid/cx-grid.scss";
</style>
